import type {
  AttemptState,
  KnowledgeCheck,
  KnowledgeCheckAttempt,
  ModuleOutline,
  ModuleProgress,
} from "@/redux/certifications/types";
import type { KnowledgeCheckState } from "@/domains/certifications/types";
import {
  ANSWER_VERDICTS,
  ATTEMPT_STATES,
  CONTENT_ITEM_TYPES,
  KNOWLEDGE_CHECK_STATES,
  PASSING_RULES,
} from "@/domains/certifications/constants";

const OPEN_STATES: AttemptState[] = [
  "SUBMITTED",
  "AI_REVIEWING",
  "PENDING_HUMAN_REVIEW",
];

export const findKnowledgeCheckId = (
  module: ModuleOutline,
  moduleProgress: ModuleProgress,
): number | null => {
  const item = module.items.find(
    (contentItem) => contentItem.type === CONTENT_ITEM_TYPES.KNOWLEDGE_CHECK,
  );
  if (!item) {
    return null;
  }
  const itemProgress = moduleProgress.items.find(
    (progress) => progress.id === item.id,
  );
  return itemProgress && itemProgress.lockReason === null
    ? (itemProgress.knowledgeCheck?.knowledgeCheckId ?? null)
    : null;
};

export const toKnowledgeCheckState = (
  state: AttemptState | null,
  attemptsRemaining: number,
): KnowledgeCheckState => {
  if (state === null) {
    return KNOWLEDGE_CHECK_STATES.NOT_STARTED;
  }
  if (state === ATTEMPT_STATES.PASSED) {
    return KNOWLEDGE_CHECK_STATES.PASSED;
  }
  if (OPEN_STATES.includes(state)) {
    return KNOWLEDGE_CHECK_STATES.UNDER_REVIEW;
  }
  return attemptsRemaining > 0
    ? KNOWLEDGE_CHECK_STATES.ANOTHER_ROUND
    : KNOWLEDGE_CHECK_STATES.FAILED;
};

export const getLatestAttempt = (
  check: KnowledgeCheck,
): KnowledgeCheckAttempt | null =>
  check.attempts.reduce<KnowledgeCheckAttempt | null>(
    (latest, attempt) =>
      !latest || attempt.attemptNumber > latest.attemptNumber
        ? attempt
        : latest,
    null,
  );

export const getKnowledgeCheckState = (
  check: KnowledgeCheck,
): KnowledgeCheckState =>
  toKnowledgeCheckState(
    getLatestAttempt(check)?.state ?? null,
    check.attemptsRemaining,
  );

export const getQuestionCount = (check: KnowledgeCheck) =>
  check.questions.templateFields.length;

export const getPassMarkPercent = (check: KnowledgeCheck) => {
  const questionCount = getQuestionCount(check);
  if (
    check.passingRule === PASSING_RULES.ALL_CORRECT ||
    check.passingCount === null
  ) {
    return 100;
  }
  return questionCount > 0
    ? Math.round((check.passingCount / questionCount) * 100)
    : 100;
};

export const getIncorrectQuestionNumbers = (
  check: KnowledgeCheck,
  attempt: KnowledgeCheckAttempt,
): number[] => {
  const incorrectFields = new Set(
    attempt.answers
      .filter((answer) => answer.verdict === ANSWER_VERDICTS.INCORRECT)
      .map((answer) => answer.field),
  );
  return [...check.questions.templateFields]
    .sort((a, b) => a.order - b.order)
    .flatMap((field, index) =>
      incorrectFields.has(field.id) ? [index + 1] : [],
    );
};
